import React, { useEffect, useState } from "react"
import classNames from "classnames"
import { useIntl } from "gatsby-plugin-intl"

import { Link } from "../Link"
import componentStyles from "./Footer.module.scss"
import Logo from "../../assets/images/logo.svg"
import ContactForm from "../ContactForm/ContactForm"

interface FooterProps {
  pathname: string
}

const menuItems = [
  {
    name: "projects",
    path: "/projects/",
    translatePath: "footer.navigation.projects",
  },
  {
    name: "expertise",
    path: "/expertise/",
    translatePath: "footer.navigation.expertise",
  },
  {
    name: "blog",
    path: "/blog/",
    translatePath: "footer.navigation.blog",
  },
  { name: "about", path: "/about/", translatePath: "footer.navigation.about" },
]

const servicesItems = [
  {
    name: "development",
    path: "/services/development",
    translatePath: "footer.navigation.development",
  },
  {
    name: "consulting",
    path: "/services/consulting",
    translatePath: "footer.navigation.consulting",
  },
  {
    name: "data-science",
    path: "/services/data-science",
    translatePath: "footer.navigation.data-science",
  },
  {
    name: "cloud",
    path: "/services/cloud",
    translatePath: "footer.navigation.cloud",
  },
]

const generalItems = [
  {
    name: "career",
    path: "/career/",
    translatePath: "footer.navigation.career",
  },
  { name: "press", path: "/press/", translatePath: "footer.navigation.press" },
  {
    name: "sitemap",
    path: "/sitemap/",
    translatePath: "footer.navigation.sitemap",
  },
  {
    name: "policy",
    path: "/personal-data-processing/",
    translatePath: "footer.navigation.policy",
  },
]

const Footer: React.FC<FooterProps> = ({ pathname }) => {
  const intl = useIntl()
  const dir = "ltr"
  const [currentPath, currentPathSetter] = useState<string>("")

  useEffect(() => {
    const path = pathname.split("/")[2]
    if (path) {
      currentPathSetter(`/${path}/`)
    }
  }, [pathname])

  const contactUsClasses = classNames(componentStyles.contactUs)
  const contactUsTitleClasses = classNames(
    componentStyles.contactUsTitle,
    "large-section-title"
  )
  const contactUsContainerClasses = classNames("container", "grid-2")

  const footerContainerClasses = classNames(
    componentStyles.footerContainer,
    "container",
    "grid-2"
  )
  const navigationListClasses = classNames(componentStyles.navigationList)

  const logoClasses = classNames(componentStyles.logo)

  const footerInf = [
     intl.formatMessage({ id: "footer.info.first" }),
     intl.formatMessage({ id: "footer.info.second" }),
     intl.formatMessage({ id: "footer.info.third" })
  ]

  return (
    <footer className={componentStyles.footer} id="contact-form">
      <section className={contactUsClasses}>
        <div className={contactUsContainerClasses}>
          <div className={componentStyles.contactUsTitleBlock}>
            <h2 className={contactUsTitleClasses}>
              {intl.formatMessage({ id: "contact-form.title" })}
            </h2>
            <p className={componentStyles.contactUsTitleDescription}>
              {intl.formatMessage({ id: "contact-form.text" })}
            </p>
          </div>
          <ContactForm className={componentStyles.contactUsForm} />
          <div className={componentStyles.contactUsImgs}>
            <img
              className={componentStyles.contactUsImgsMCube}
              src="/images/cube-medium.png"
              alt="Medium size orange graphic cube image"
              width="57"
              height="75"
            />
            <picture>
              <source
                type="image/webp"
                srcSet='/images/cube-large.webp'
              />
              <source
                type="image/png"
                srcSet='/images/cube-large.png'
              />
              <img
                className={componentStyles.contactUsImgsLCube}
                src="/images/cube-large.png"
                alt="Large size orange graphic cube image"
                width="105"
                height="135"
              />
            </picture>
            <picture>
              <source srcSet="/images/talk-illustration.webp" type="image/webp"/>
              <source srcSet="/images/talk-illustration.png" type="image/png"/>
              <img
                className={componentStyles.contactUsImgsMainImg}
                src="/images/talk-illustration.png"
                alt="Illustration with meeting"
                width="380"
                height="269"
              />
            </picture>
            <img
              className={componentStyles.contactUsImgsSmCube}
              src="/images/cube-small.png"
              alt="Small size orange graphic cube image"
              width="40"
              height="52"
            />
            <a
              className={componentStyles.contactUsMailInfo}
              href={`mailto:${intl.formatMessage({
                id: "contact-form.mail",
              })}`}
            >
              {intl.formatMessage({ id: "contact-form.mail" })}
            </a>
            <div className={componentStyles.contactUsPhone}>
              <a
                href={`tel:${intl.formatMessage({ id: "contact-form.phone" })}`}
              >
                <span dir="ltr">
                  {intl.formatMessage({ id: "contact-form.phone" })}
                </span>
              </a>
              <a
                className={componentStyles.whatsappIcon}
                href="https://wa.me/message/Q6C26SM43VX7L1"
                target="_blank"
              >
                <img
                  src="/images/whatsapp-logo.svg"
                  alt="Whatsapp logo"
                  width="24"
                  height="24"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className={footerContainerClasses}>
        <div>
          <Link to="/" className={logoClasses}>
            <img
              className={componentStyles.logoImage}
              src={Logo}
              alt="Inventale logo"
              width="172"
              height="40"
            />
          </Link>
          <div>
            <a
              className={classNames(
                componentStyles.companyInfo,
                componentStyles.address
              )}
              href={intl.formatMessage({ id: "footer.info.address.url" })}
            >
              {intl.formatMessage({ id: "footer.info.address.text" })}
            </a>
            {
              footerInf.map(infText => {
                const infTextTrim = infText.trim();
                if (infTextTrim !== '') {
                  return (
                    <p className={componentStyles.companyInfo}>
                      {infTextTrim}
                    </p>
                  )
                }
              })
            }
            <div className={componentStyles.socialBlock}>
              <a href="https://www.facebook.com/inventale" className={componentStyles.socialBlockLink} target="blank">
                <svg
                  className={componentStyles.socialBlockIcon}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.05196 0.0126777C3.00031 0.0230422 2.84279 0.0549331 2.70194 0.0835411C1.68108 0.290597 0.687364 1.13425 0.250767 2.16451C-0.0144124 2.79018 0.000142407 2.22042 0.000142407 11.9897C0.000142407 21.759 -0.0144124 21.1892 0.250767 21.8149C0.603556 22.6474 1.3228 23.3715 2.13641 23.7133C2.76992 23.9795 2.55451 23.9668 6.72587 23.9837L10.5876 23.9993V19.7532V15.5071H9.17906H7.77053V13.3967V11.2862H9.17558H10.5806L10.5988 9.51583C10.6158 7.8659 10.6241 7.7199 10.7207 7.37023C11.1261 5.90409 12.2845 4.74664 13.7568 4.33661C14.1195 4.23559 14.2172 4.23146 16.609 4.21566L19.0857 4.19929V6.31234V8.42544H16.9494H14.8132V9.85584V11.2862H16.9532H19.0932L19.0666 11.4152C19.052 11.4862 18.9021 12.3778 18.7336 13.3967C18.5651 14.4155 18.4152 15.3072 18.4006 15.3781L18.374 15.5071H16.5936H14.8132V19.7535V24L17.9706 23.9836C20.6585 23.9697 21.163 23.9565 21.3628 23.8946C22.6755 23.4879 23.6048 22.5385 23.9264 21.2756C24.0245 20.8903 24.0245 3.08916 23.9264 2.70384C23.6516 1.625 22.8583 0.69125 21.8558 0.266914C21.2007 -0.0103963 21.945 0.00991067 12.137 0.00151587C7.19186 -0.00275188 3.1036 0.00231314 3.05196 0.0126777Z" fill="#A3A3A3"
                    />
                </svg>
              </a>
              <a href="https://www.instagram.com/invenpro.tech/" className={componentStyles.socialBlockLink} target="blank">
                <svg
                    className={componentStyles.socialBlockIcon}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.65944 0.0121608C4.20715 0.168718 2.88021 0.814764 1.83122 1.87606C0.898466 2.81972 0.363707 3.82068 0.0841637 5.1462C0.005589 5.51881 0 5.97459 0 12.002C0 18.0295 0.005589 18.4853 0.0841637 18.8579C0.364036 20.1848 0.900298 21.1879 1.83122 22.1259C2.77646 23.0782 3.80333 23.6332 5.14281 23.9156C5.51549 23.9942 5.97111 23.9998 11.9999 23.9998C19.2119 23.9998 18.7357 24.0203 19.7607 23.6661C20.6841 23.3469 21.3826 22.9051 22.1277 22.1689C23.073 21.2349 23.633 20.1978 23.9156 18.8579C23.9942 18.4853 23.9998 18.0297 23.9998 12.002C23.9998 4.84262 24.0189 5.31741 23.6906 4.31843C23.3779 3.36706 22.9106 2.62297 22.1457 1.85812C21.2167 0.929342 20.2855 0.4152 18.9744 0.107344C18.5558 0.00906161 18.4891 0.00802854 12.1878 0.00126661C8.6876 -0.00249002 5.74985 0.0023936 5.65944 0.0121608ZM16.6261 4.33782C18.1405 4.76303 19.2666 5.8972 19.6666 7.40018L19.7728 7.79932V12.002V16.2048L19.6666 16.6039C19.2635 18.1185 18.1174 19.2644 16.6026 19.6674L16.2034 19.7736H11.9999C8.14622 19.7736 7.76973 19.7667 7.47568 19.6912C5.91156 19.2896 4.74219 18.1408 4.33321 16.6039L4.22697 16.2048V12.002V7.79932L4.33321 7.40018C4.76286 5.78568 6.05734 4.57557 7.6712 4.27974C7.96544 4.22583 8.78256 4.21545 12.1408 4.22306C16.244 4.23236 16.2509 4.2325 16.6261 4.33782ZM7.73545 5.71073C7.22135 5.84541 6.85737 6.05845 6.45627 6.45943C6.04945 6.86618 5.84097 7.22644 5.70523 7.75687C5.61651 8.1037 5.61248 8.28937 5.61248 12.002C5.61248 15.7147 5.61651 15.9004 5.70523 16.2472C5.84097 16.7777 6.04945 17.1379 6.45627 17.5447C6.8631 17.9514 7.22342 18.1599 7.75395 18.2956C8.10085 18.3843 8.28655 18.3883 11.9999 18.3883C15.7132 18.3883 15.8989 18.3843 16.2458 18.2956C16.7764 18.1599 17.1367 17.9514 17.5435 17.5447C17.9503 17.1379 18.1588 16.7777 18.2946 16.2472C18.3833 15.9004 18.3873 15.7147 18.3873 12.002C18.3873 8.28937 18.3833 8.1037 18.2946 7.75687C18.1588 7.22644 17.9503 6.86618 17.5435 6.45943C17.1367 6.05268 16.7764 5.84423 16.2458 5.70852C15.8984 5.61973 15.7178 5.61588 11.9806 5.61836C8.2936 5.62081 8.05928 5.62592 7.73545 5.71073ZM16.369 7.35383C16.5774 7.46015 16.7435 7.73067 16.7435 7.96367C16.7435 8.32098 16.3964 8.66804 16.039 8.66804C15.806 8.66804 15.5354 8.50195 15.429 8.2936C15.2501 7.94292 15.3547 7.57439 15.6911 7.37083C15.916 7.23475 16.1252 7.22949 16.369 7.35383ZM12.6894 8.55389C13.393 8.69922 13.9817 9.01891 14.4854 9.5293C14.9464 9.99634 15.243 10.5068 15.4147 11.1287C15.5232 11.5216 15.5232 12.4825 15.4147 12.8754C15.052 14.1886 14.006 15.1783 12.6945 15.4491C10.2571 15.9525 8.0513 13.7474 8.55163 11.3074C8.94079 9.40974 10.8101 8.16578 12.6894 8.55389ZM11.5048 9.95445C11.1158 10.0489 10.8188 10.219 10.5171 10.5201C9.68338 11.352 9.68338 12.6535 10.5171 13.4833C11.355 14.3173 12.6518 14.3179 13.4814 13.4846C14.3155 12.6468 14.3161 11.3528 13.4827 10.5195C12.9466 9.98352 12.229 9.77855 11.5048 9.95445Z" fill="#A3A3A3"
                      />
                  </svg>
              </a>
              <a href="https://www.linkedin.com/company/76620891/" className={componentStyles.socialBlockLink} target="blank">
                <svg
                    className={componentStyles.socialBlockIcon}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.6774 0.0357333C1.36795 0.255894 0.243557 1.40703 0.0393876 2.73654C0.00554703 2.95689 -0.00599912 5.96166 0.0028717 12.2149L0.0159198 21.3707L0.148043 21.7463C0.516863 22.7946 1.2209 23.4989 2.26883 23.8678L2.64431 24H12.008H21.3716L21.7471 23.8674C22.8306 23.4849 23.5776 22.705 23.9139 21.6054C23.9962 21.3361 24 20.9108 24 11.9801V2.63658L23.8679 2.26096C23.4991 1.21265 22.795 0.508358 21.7471 0.139405L21.3716 0.00723311L12.1488 0.000565841C6.94281 -0.00319037 2.81769 0.0121162 2.6774 0.0357333ZM7.10155 4.56318C7.70143 4.71949 8.1844 5.12549 8.45268 5.69901C8.64169 6.10313 8.68464 6.66891 8.55904 7.10111C8.30554 7.97335 7.51308 8.57706 6.6236 8.57552C5.80923 8.57411 5.09092 8.12886 4.75416 7.41668C4.01849 5.86105 5.44191 4.13075 7.10155 4.56318ZM16.3133 9.33089C17.9507 9.51353 18.9309 10.4533 19.2787 12.1739C19.4382 12.9634 19.4697 13.6713 19.4702 16.4805L19.4707 19.1968L19.3111 19.3564L19.1516 19.516L17.8679 19.5158C16.6745 19.5157 16.5735 19.5092 16.4329 19.4234C16.3497 19.3726 16.2708 19.283 16.2576 19.224C16.2444 19.1651 16.2266 17.8809 16.218 16.3702C16.2044 13.9471 16.1931 13.5903 16.122 13.3418C15.9958 12.9001 15.8226 12.5863 15.5933 12.3844C15.3115 12.1363 15.0415 12.0506 14.5416 12.0506C14.1827 12.0506 14.1016 12.0673 13.8499 12.1931C13.5049 12.3656 13.2629 12.6229 13.0841 13.0073C12.7866 13.6468 12.7903 13.6048 12.7617 16.5685L12.7355 19.2788L12.6286 19.3856C12.5235 19.4908 12.5015 19.4928 11.2576 19.5064C9.8684 19.5217 9.77373 19.5063 9.66705 19.2483C9.62687 19.1511 9.61598 17.8903 9.62523 14.4059L9.63771 9.69482L9.76941 9.58147L9.90112 9.46818H11.1923C11.9826 9.46818 12.5183 9.48682 12.5732 9.51621C12.7256 9.59781 12.7785 9.75661 12.8044 10.2109L12.8293 10.6484L13.0101 10.4109C13.6445 9.57697 14.8845 9.17153 16.3133 9.33089ZM8.01801 9.60138C8.10682 9.69026 8.16112 9.80365 8.18093 9.94179C8.19726 10.0557 8.20435 12.1773 8.1967 14.6564C8.18351 18.9052 8.17788 19.171 8.09827 19.2883C7.94963 19.5072 7.84604 19.5216 6.52574 19.5065L5.29814 19.4926L5.16809 19.3624L5.03803 19.2322L5.02488 14.5732C5.0177 12.0108 5.02381 9.86488 5.03854 9.80459C5.05323 9.74426 5.1203 9.64392 5.18756 9.58157L5.30988 9.46818H6.59737H7.88486L8.01801 9.60138Z" fill="#A3A3A3"
                      />
                  </svg>
              </a>
            </div>
          </div>
        </div>
        <nav className={componentStyles.navigation}>
          <div className={componentStyles.navigationBlock}>
            <h4 className={componentStyles.navigationTitle}>
              {intl.formatMessage({ id: "footer.navigation.menu-title" })}
            </h4>
            <ul className={navigationListClasses}>
              {menuItems.map((item, index) => (
                <li
                  key={index}
                  className={componentStyles.navigationItem}
                  data-name={item.name}
                >
                  <Link
                    to={
                      item.name === "blog"
                        ? item.path.replace("{locale}", intl.locale)
                        : item.path
                    }
                  >
                    {intl.formatMessage({ id: item.translatePath })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className={componentStyles.navigationBlock}>
            <h4 className={componentStyles.navigationTitle}>
              {intl.formatMessage({ id: "footer.navigation.services-title" })}
            </h4>
            <ul className={navigationListClasses}>
              {servicesItems.map((item, index) => (
                <li
                  key={index}
                  className={componentStyles.navigationItem}
                  data-name={item.name}
                >
                  <Link to={item.path}>
                    {intl.formatMessage({ id: item.translatePath })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className={componentStyles.navigationBlock}>
            <ul className={navigationListClasses}>
              {generalItems.map((item, index) => (
                <li
                  key={index}
                  className={componentStyles.navigationItem}
                  data-name={item.name}
                >
                  <Link to={item.path}>
                    {intl.formatMessage({ id: item.translatePath })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </section>
    </footer>
  )
}

export default Footer
